export const NavigatorControllerFactory = ({
  navigatorService,
}: {
  navigatorService: INavigatorService;
}) => {
  const createInitialVisit = async ({
    patientId,
    clinicId,
    visitData,
  }: {
    patientId: number;
    clinicId: number;
    visitData: {
      type: number;
      shortlisted: boolean;
      notes: Record<string, unknown>;
      file_link?: string;
      total_time?: number;
      context?: Record<string, unknown>;
      doc_type?: string;
    };
  }) => {
    try {
      // Transform dates in the visit data
      const transformedVisitData = {
        ...visitData,
        notes: visitData.notes || {},
        context: visitData.context || {},
        doc_type: visitData.doc_type || "np_visit",
      };

      const result = await navigatorService.createInitialVisit({
        patientId,
        clinicId,
        visitData: transformedVisitData,
      });

      if (result.success) {
        return {
          success: true,
          data: result.data,
        };
      }

      return {
        success: false,
        error: result.error,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getIPVNotesByStatus = async ({
    clinicId,
    status,
  }: {
    clinicId: number;
    status: number;
  }) => {
    const result = await navigatorService.getIPVNotesByStatus({
      clinicId,
      status,
    });
    if (result.success && result.data) {
      return {
        success: true,
        data: result.data,
      };
    }
    return {
      success: false,
      error: result.error,
    };
  };

  const getIPVNotesByStatusv3 = async ({
    clinicId,
    status,
  }: {
    clinicId: number;
    status: number;
  }) => {
    const result = await navigatorService.getIPVNotesByStatusv3({
      clinicId,
      status,
    });
    if (result.success && result.data) {
      return {
        success: true,
        data: result.data,
      };
    }
    return {
      success: false,
      error: result.error,
    };
  };

  const putIPVNote = async ({
    patientId,
    clinicId,
    ipvNoteId,
    visitData,
  }: {
    patientId: number;
    clinicId: number;
    ipvNoteId: number;
    visitData: {
      type: number;
      shortlisted: boolean;
      notes: Record<string, unknown>;
      file_link: string;
      total_time: number;
      context?: Record<string, unknown>;
      doc_type?: string; // NEW: Support doc_type
    };
  }) => {
    try {
      // Transform visit data if needed (similar to createInitialVisit)
      const transformedVisitData = {
        ...visitData,
        notes: visitData.notes || {},
        context: visitData.context || {},
        doc_type: visitData.doc_type || "np_visit", // NEW: Default to np_visit for backward compatibility
      };

      const result = await navigatorService.putIPVNote({
        patientId,
        clinicId,
        ipvNoteId,
        visitData: transformedVisitData,
      });
      if (result.success && result.data) {
        return {
          success: true,
          data: result.data,
        };
      }

      return {
        success: false,
        error: result.error,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getIPVNotesByPatientID = async ({
    patientId,
    clinicId,
  }: {
    patientId: number;
    clinicId: number;
  }) => {
    const result = await navigatorService.getIPVNotesByPatientID({
      patientId,
      clinicId,
    });

    if (result.success && result.data) {
      return {
        success: true,
        data: result.data,
      };
    }

    return {
      success: false,
      error: result.error,
    };
  };
  const deleteIPVNote = async ({
    patientId,
    clinicId,
    ipvNoteId,
  }: {
    patientId: number;
    clinicId: number;
    ipvNoteId: number;
  }) => {
    const result = await navigatorService.deleteIPVNote({
      patientId,
      clinicId,
      ipvNoteId,
    });

    if (result.success && result.data) {
      return {
        success: true,
        data: result.data,
      };
    }

    return {
      success: false,
      error: result.error,
    };
  };

  const deleteIPVDraftAndSession = async ({
    patientId,
    clinicId,
    ipvNoteId,
    sessionId,
  }: {
    patientId: number;
    clinicId: number;
    ipvNoteId?: number;
    sessionId?: string;
  }) => {
    try {
      const result = await navigatorService.deleteIPVDraftAndSession({
        patientId,
        clinicId,
        ipvNoteId,
        sessionId,
      });

      if (result.success) {
        return {
          success: true,
          data: result.data,
        };
      }

      return {
        success: false,
        error: result.error,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPatientsForNavigator = async ({
    clinicId,
  }: {
    clinicId: number;
  }) => {
    const result = await navigatorService.getPatientsForNavigator({
      clinicId,
    });
    if (result.success && result.data) {
      return {
        success: true,
        data: result.data,
      };
    }
    return {
      success: false,
      error: result.error,
    };
  };

  const updatePatientExtraInfo = async ({
    patientId,
    clinicId,
    extraInfo,
  }: {
    patientId: number;
    clinicId: number;
    extraInfo: Record<string, unknown>;
  }) => {
    try {
      const result = await navigatorService.updatePatientExtraInfo({
        patientId,
        clinicId,
        extraInfo,
      });

      if (result.success) {
        return {
          success: true,
          data: result.data,
        };
      }

      return {
        success: false,
        error: result.error,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPatientVisitNotes = async ({
    clinicId,
    patientId,
  }: {
    clinicId: number;
    patientId: number;
  }) => {
    const result = await navigatorService.getPatientVisitNotes({
      clinic_id: clinicId,
      patient_id: patientId,
    });
    if (result.success && result.data) {
      return {
        success: true,
        data: result.data,
      };
    }
  };

  const storePreChartNote = async ({
    clinicId,
    patientId,
    apptNote,
  }: {
    clinicId: number;
    patientId: number;
    apptNote: string;
  }) => {
    const result = await navigatorService.storePreChartNote({
      clinicId,
      patientId,
      apptNote,
    });
    if (result.success && result.data) {
      return {
        success: true,
        data: result.data,
      };
    }
    return {
      success: false,
      error: result.error,
    };
  };

  return {
    createInitialVisit,
    putIPVNote,
    getIPVNotesByPatientID,
    deleteIPVNote,
    deleteIPVDraftAndSession,
    getIPVNotesByStatus,
    getIPVNotesByStatusv3,
    getPatientsForNavigator,
    updatePatientExtraInfo,
    getPatientVisitNotes,
    storePreChartNote,
  };
};

// Controller interfaces
export interface ICreateInitialVisitController {
  (params: {
    patientId: number;
    clinicId: number;
    visitData: {
      type: number;
      shortlisted: boolean;
      notes: Record<string, unknown>;
      file_link?: string;
      total_time?: number;
      doc_type?: "np_visit" | "at_risk_review"; // NEW: Support doc_type
    };
  }): Promise<{
    success: boolean;
    data?: IInitialPatientVisit;
    error?: { message: string };
  }>;
}

export interface IPutIPVNoteController {
  (params: {
    patientId: number;
    clinicId: number;
    ipvNoteId: number;
    visitData: {
      type: number;
      shortlisted: boolean;
      notes: Record<string, unknown>;
      file_link: string;
      total_time: number;
      doc_type?: "np_visit" | "at_risk_review"; // NEW: Support doc_type
    };
  }): Promise<{
    success: boolean;
    data?: IInitialPatientVisit;
    error?: { message: string };
  }>;
}

export interface IDeleteIPVNoteController {
  (params: { patientId: number; clinicId: number }): Promise<{
    success: boolean;
    data?: IInitialPatientVisit;
    error?: { message: string };
  }>;
}

export interface IDeleteIPVDraftAndSessionController {
  (params: {
    patientId: number;
    clinicId: number;
    ipvNoteId?: number;
    sessionId?: string;
  }): Promise<{
    success: boolean;
    data?: IDeleteIPVDraftAndSessionResponse;
    error?: { message: string };
  }>;
}

export interface IUpdatePatientExtraInfoController {
  (params: {
    patientId: number;
    clinicId: number;
    extraInfo: Record<string, unknown>;
  }): Promise<{
    success: boolean;
    data?: Record<string, unknown>;
    error?: { message: string };
  }>;
}

export interface IStorePreChartNoteController {
  (params: { clinicId: number; patientId: number; apptNote: string }): Promise<{
    success: boolean;
    data?: Record<string, unknown>;
    error?: { message: string };
  }>;
}

// this is return type of the controller method

export type INavigatorController = ReturnType<
  typeof NavigatorControllerFactory
>;

export interface IGetPatientsForNavigatorController {
  (params: { clinicId: number }): Promise<{
    success: boolean;
    data?: IGetPatientsForNavigatorResponse;
    error?: { message: string };
  }>;
}
